import { createContext, useContext, type ReactNode } from 'react';
import type { ValidCurrency } from '@/types/plans';

/**
 * SSR-resolved targeting attributes for the pricing-test cohort. Provided by
 * the pricing page so that `usePricingCohort` can evaluate the same flag on
 * first paint that the server-side `withGrowthBook` evaluated when stamping
 * the prefetched pricing data.
 *
 * Without this, the hook's initial render reads the wrong flag (default
 * `USD` / empty country) before `useSettings` resolves and fires a stray
 * `experiment_viewed` against a flag the user doesn't belong to — which is
 * what was inflating the USD ROW enrolment numbers.
 *
 * When no provider is present (any non-pricing page), the hook falls back to
 * its previous defaults — this is purely additive.
 */
interface PricingCohortContextValue {
  initialCountry: string;
  initialCurrency: ValidCurrency;
}
const PricingCohortContext = createContext<PricingCohortContextValue | null>(null);
interface PricingCohortProviderProps {
  children: ReactNode;
  initialCountry: string;
  initialCurrency: ValidCurrency;
}
export function PricingCohortProvider({
  initialCurrency,
  initialCountry,
  children
}: PricingCohortProviderProps) {
  return <PricingCohortContext.Provider value={{
    initialCurrency,
    initialCountry
  }} data-sentry-element="PricingCohortContext.Provider" data-sentry-component="PricingCohortProvider" data-sentry-source-file="PricingCohortContext.tsx">
      {children}
    </PricingCohortContext.Provider>;
}
export function usePricingCohortInitial(): PricingCohortContextValue | null {
  return useContext(PricingCohortContext);
}